import { useState, useEffect, useCallback } from "react";
import { useRouter } from "next/router";

const SIGNUP_POPUP_DELAY = 2 * 60 * 1000;
const POPUP_TIMESTAMP_KEY = "signupPopupTimestamp";
const ONE_WEEK_MS = 7 * 24 * 60 * 60 * 1000;

interface UseSignUpPromotionProps {
  isLoggedIn: boolean;
  enabled?: boolean;
}

interface UseSignUpPromotionReturn {
  showSignUpPopup: boolean;
  handleClose: () => void;
  handleSignUp: () => void;
}

export const useSignUpPromotion = ({
  isLoggedIn,
  enabled = true,
}: UseSignUpPromotionProps): UseSignUpPromotionReturn => {
  const [showPopup, setShowPopup] = useState(false);
  const router = useRouter();
  const wasRecentlyDismissed = useCallback((): boolean => {
    if (typeof window === "undefined") return false;

    const timestamp = localStorage.getItem(POPUP_TIMESTAMP_KEY);
    if (!timestamp) return false;

    const elapsed = Date.now() - parseInt(timestamp, 10);
    return elapsed < ONE_WEEK_MS;
  }, []);

  const saveDismissal = useCallback((): void => {
    if (typeof window === "undefined") return;
    localStorage.setItem(POPUP_TIMESTAMP_KEY, Date.now().toString());
  }, []);

  const handleClose = useCallback((): void => {
    setShowPopup(false);
    saveDismissal();
  }, [saveDismissal]);

  const handleSignUp = useCallback((): void => {
    setShowPopup(false);
    saveDismissal();
    router.push({
      pathname: "/auth/signup",
      query: { next_url: router.asPath },
    });
  }, [router, saveDismissal]);

  useEffect(() => {
    if (typeof window === "undefined") return;

    const recentlyDismissed = wasRecentlyDismissed();
    const isOnAuthPage = router.pathname?.startsWith("/auth/");
    const shouldShow = enabled && !isLoggedIn && !recentlyDismissed && !isOnAuthPage;

    if (!shouldShow) {
      return;
    }

    const timer = setTimeout(() => {
      setShowPopup(true);
    }, SIGNUP_POPUP_DELAY);

    return () => {
      clearTimeout(timer);
    };
  }, [enabled, isLoggedIn, wasRecentlyDismissed, router.pathname]);

  return {
    showSignUpPopup: showPopup,
    handleClose,
    handleSignUp,
  };
};
